import type { Breakpoint } from "@mui/material";
import type React from "react";
import { type CSSProperties, memo, useEffect, useState } from "react";
import type { Department } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRoleColors } from "../../hooks/useRoleColors";
import { trackEvent } from "../../utils/analytics";
import type { NurseFullRole, NurseRole } from "../../utils/models";
import { nurseRoleDisplayName, rolesMap } from "../../utils/utilFunctions";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";

interface RoleSelectorProps {
	onRolesChange?: (selectedRoles: NurseFullRole[]) => void;
	chosenDepartment: Department;
	isHorizontal?: boolean;
	selectedRoles?: NurseFullRole[];
	width?: CSSProperties["width"] | Partial<Record<Breakpoint, CSSProperties["width"]>>;
}

const RoleSelector: React.FC<RoleSelectorProps> = memo(({ onRolesChange, chosenDepartment, selectedRoles, width }) => {
	const roleColor = useRoleColors();
	const { departmentNurseTypes } = useHospitalData();

	const defaultRoles = Object.entries(rolesMap)
		.filter(([, fullRole]) => departmentNurseTypes.includes(fullRole))
		.map(([role, fullRole]) => ({
			label: (nurseRoleDisplayName[role as NurseRole] ?? role) as NurseRole,
			value: fullRole as NurseFullRole,
			color: roleColor(fullRole).dot,
		}));

	const [selectedRolesValues, setSelectedRolesValues] = useState<NurseFullRole[]>(selectedRoles ?? defaultRoles.map((role) => role.value));

	useEffect(() => {
		trackEvent("role_selector_mounted", {
			department_id: chosenDepartment?.department_id,
			available_roles: departmentNurseTypes.length,
		});
	}, []);

	const handleContractTypeChange = (value: NurseFullRole[]) => {
		trackEvent("role_selector_changed", {
			department_id: chosenDepartment?.department_id,
			previous_selection: selectedRolesValues.join(","),
			new_selection: value.join(","),
		});

		onRolesChange?.(value);
		setSelectedRolesValues(value);
	};

	return (
		<div className="flex flex-col space-y-2" style={{ display: "flex" }}>
			<SystemDropdown
				label={"Roles"}
				fullWidth
				multiple
				items={defaultRoles}
				selectedItems={selectedRolesValues}
				onChangeMultiple={(value) => handleContractTypeChange(value)}
				selectSx={{ width: width ?? "200px" }}
				renderAllValueText="All"
			/>
		</div>
	);
});

export default RoleSelector;
